import { useState } from 'react';
import { message } from 'antd';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { OrderType } from '../graphql/types/OrderType';
import { BATCH_SET_DELIVERED_STATUS, BatchSetDeliveredStatusInput, BatchSetDeliveredStatusResponse } from '../pages/wms/parcels/graphql/mutations/batchSetDeliveredStatus';
import { SetDeliverStatusData } from '../components/SetDeliverStatusModal';

interface UseSetDeliverStatusProps {
    warehouseId?: string | null;
    handleFilter: () => void;
    onClearSelection?: () => void; // 添加清空选择的回调函数
}

export const useSetDeliverStatus = ({ warehouseId, handleFilter, onClearSelection }: UseSetDeliverStatusProps) => {
    const { t } = useTranslation();
    const [deliverStatusVisible, setDeliverStatusVisible] = useState(false);
    const [deliverStatusLoading, setDeliverStatusLoading] = useState(false);
    const [filteredDeliverRows, setFilteredDeliverRows] = useState<OrderType[]>([]);
    const [failedOrdersVisible, setFailedOrdersVisible] = useState(false);
    const [failedOrders, setFailedOrders] = useState<Array<{trackingNumber: string, errorCode?: string, message?: string}>>([]);
    const [batchSetDeliveredStatus] = useMutation<BatchSetDeliveredStatusResponse, {input: BatchSetDeliveredStatusInput}>(BATCH_SET_DELIVERED_STATUS);

    const handleSetDeliverStatus = (selectedRows: OrderType[]) => {
        // 清空之前选中的数据
        setFilteredDeliverRows([]);
        
        if (selectedRows.length < 1) {
            message.warning(t('wms.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        
        // 状态条件判断：
        // 1. 派送中 DELIVERY_IN_PROGRESS
        // 2. 妥投失败 DELIVERY_FAILED 且异常原因为 ERDL001、ERDL002 或 ERDL003
        const allowedExceptionReasonCodes = ['ERDL001', 'ERDL002', 'ERDL003'];
        const filterRows = selectedRows.filter(row => {
            const isDeliveryInProgress = row.overallStatus === "DELIVERY_IN_PROGRESS";
            const isDeliveryFailedWithAllowedReason = 
                row.overallStatus === "DELIVERY_FAILED" &&
                row.exceptionReasonCode &&
                allowedExceptionReasonCodes.includes(row.exceptionReasonCode);
            return isDeliveryInProgress || isDeliveryFailedWithAllowedReason;
        });
        
        if (filterRows.length < 1) {
            message.warning(t('wms.parcels.messages.selectParcelsForDeliverStatus'));
            return;
        }
        
        if (filterRows.length > 20) {
            message.warning(t('wms.parcels.messages.max20OrdersAllowed'));
            return;
        }
        
        // 保存过滤后的数据
        setFilteredDeliverRows(filterRows);
        setDeliverStatusVisible(true);
    };

    const handleSetDeliverStatusConfirm = async (formData: SetDeliverStatusData) => {
        try {
            setDeliverStatusLoading(true);
            
            // 构建API调用参数
            const input: BatchSetDeliveredStatusInput = {
                warehouseId: warehouseId,
                orderList: formData.orderList
            };
            
            const result = await batchSetDeliveredStatus({
                variables: { input }
            });
            
            const batchResult = result.data?.batchSetDeliveredStatus;
            
            if (batchResult) {
                // 根据finalStatus判断结果
                if (batchResult.finalStatus === 1) {
                    // 全部成功
                    message.success(t('wms.parcels.messages.setDeliverStatusSuccessfully'));
                    setDeliverStatusVisible(false);
                    setFilteredDeliverRows([]); // 清空过滤后的数据
                    onClearSelection?.(); // 清空选中的行
                    handleFilter();
                } else if (batchResult.finalStatus === 2 || batchResult.finalStatus === -1) {
                    // 部分成功 - 显示失败订单弹窗
                    const failedOrdersList = batchResult.results?.filter(result => !result.success) || [];
                    setFailedOrders(failedOrdersList.map(result => ({
                        trackingNumber: result.trackingNumber || '',
                        errorCode: result.errorCode,
                        message: result.message
                    })));
                    setFailedOrdersVisible(true);
                    setDeliverStatusVisible(false);
                    setFilteredDeliverRows([]); // 清空过滤后的数据
                    onClearSelection?.(); // 清空选中的行
                    handleFilter();
                } else {
                    // 未知状态
                    message.error(t('wms.parcels.messages.setDeliverStatusFailed'));
                    setDeliverStatusVisible(false);
                    setFilteredDeliverRows([]); // 清空过滤后的数据
                }
            } else {
                message.error(t('wms.parcels.messages.setDeliverStatusFailed'));
                setDeliverStatusVisible(false);
                setFilteredDeliverRows([]); // 清空过滤后的数据
            }
        } catch (error) {
            console.error('set deliver status failed:', error);
            message.error(t('wms.parcels.messages.setDeliverStatusFailed'));
            setDeliverStatusVisible(false);
            setFilteredDeliverRows([]); // 清空过滤后的数据
        } finally {
            setDeliverStatusLoading(false);
        }
    };

    return {
        deliverStatusVisible,
        deliverStatusLoading,
        filteredDeliverRows,
        handleSetDeliverStatus,
        handleSetDeliverStatusConfirm,
        setDeliverStatusVisible,
        failedOrdersVisible,
        failedOrders,
        setFailedOrdersVisible,
        setFailedOrders
    };
};
